import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { SiteFooter } from '../../../components/SiteFooter'
import { SiteHeader } from '../../../components/SiteHeader'
import { ReceiptView } from '../../../components/receipt/ReceiptView'
import { useLocale } from '../../../components/use-locale'
import { fmt, getDict, intlTag, parseLocale } from '../../../i18n'
import { seoHead } from '../../../lib/seo'
import { SITE_ORIGIN } from '../../../lib/site'
import { getLogFn } from '../../../server-fns/log'

export const Route = createFileRoute('/{-$locale}/log/$id')({
  beforeLoad: ({ params }) => {
    if (!parseLocale(params.locale)) throw notFound()
  },
  loader: async ({ params }) => {
    const log = await getLogFn({ data: params.id })
    if (!log) throw notFound()
    return { log }
  },
  head: ({ params, loaderData }) => {
    const locale = parseLocale(params.locale) ?? 'ja'
    const t = getDict(locale)
    const log = loaderData?.log
    if (!log) return { meta: [{ title: t.meta.notFoundTitle }] }
    const minutes = Math.round(log.focusSeconds / 60)
    const date = new Intl.DateTimeFormat(intlTag(locale), { dateStyle: 'long' }).format(log.endedAt)
    return seoHead({
      origin: SITE_ORIGIN,
      locale,
      path: `/log/${log.id}`,
      title: fmt(t.meta.logTitle, { name: log.user.name, minutes }),
      description: log.note || fmt(t.meta.logDescription, { name: log.user.name, minutes, date }),
      image: `/cards/${log.id}.png`,
      type: 'article',
    })
  },
  component: LogPage,
})

function LogPage() {
  const { log } = Route.useLoaderData()
  const { t, locale, path } = useLocale()
  return (
    <>
      <SiteHeader variant="solid" />
      <main id="main" className="log-page">
        <h1 className="sr-only">{fmt(t.log.by, { name: log.user.name })}</h1>
        <svg className="log-page__lamp" viewBox="0 0 120 160" aria-hidden="true">
          <line x1="60" y1="0" x2="60" y2="88" stroke="currentColor" strokeWidth="1.5" />
          <path d="M22 132 C22 106 39 90 60 90 C81 90 98 106 98 132 Z" fill="currentColor" />
          <path d="M22 132 C22 106 39 90 60 90" fill="none" stroke="#b07a3a" strokeWidth="1" opacity=".7" />
          <ellipse cx="60" cy="134" rx="15" ry="7" fill="#ffe6bf" />
        </svg>
        <div className="log-page__light" aria-hidden="true" />
        <ReceiptView
          tilt={-1.5}
          data={{
            locale,
            id: log.id,
            startedAt: log.startedAt,
            endedAt: log.endedAt,
            plannedMinutes: log.plannedMinutes,
            focusSeconds: log.focusSeconds,
            note: log.note,
            name: log.user.name,
            timeZone: log.user.timezone,
          }}
        />
        <div className="log-page__actions">
          <Link to={path(`/u/${log.user.handle}`)} className="btn btn--ghost">
            {fmt(t.log.viewProfile, { name: log.user.name })}
          </Link>
          <Link to={path('/cafe')} className="btn btn--lamp">
            {t.log.cta}
          </Link>
        </div>
      </main>
      <SiteFooter />
    </>
  )
}
